import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CaretDownIcon } from "../../assets/icons/caret-down.svg";
import { ReactComponent as CaretUpIcon } from "../../assets/icons/caret-up.svg";
import { ReactComponent as ArchiveIcon } from "../../assets/icons/archive.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendar-blank.svg";
import { ReactComponent as FilterLinesIcon } from "../../assets/icons/filter-lines.svg";
import AssetGraph from "../../components/AssetGraphComponents/AssetGraph";

// This component represents the insights cards section of the Asset Dashboard. it includes
// filtering options, individual insight cards, and a detailed insight card with a graph.

type SimpleInsightCardProps = {
  assetId?: string;
  groupId?: string | null;
  title: string;
  description: string;
  selectedAsset?: any;
};

const SWIPE_ACTION_WIDTH = 160; // Width of the revealed action area

// SwipeReveal component to handle swipe-to-reveal actions
const SwipeReveal = ({ children }: { children: React.ReactNode }) => {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startDragRef = useRef(0);

  // Handlers for pointer events. 
  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("button, a, input, [data-no-swipe]")) {
      return;
    }
    setIsDragging(true);
    startXRef.current = event.clientX;
    startDragRef.current = dragX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  // Update drag position on pointer move
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const delta = event.clientX - startXRef.current;
    const next = Math.max(-SWIPE_ACTION_WIDTH, Math.min(0, startDragRef.current + delta));
    setDragX(next);
  };

  // Finalize drag position on pointer up/cancel
  const onPointerUp = () => {
    setIsDragging(false);
    setDragX((prev) => (Math.abs(prev) > SWIPE_ACTION_WIDTH / 2 ? -SWIPE_ACTION_WIDTH : 0));
  };

  // Render the swipeable container with action area
  return (
    <div className="relative overflow-hidden rounded-[8px] border border-eai-bg-lt2 bg-white">
      <div className="absolute inset-y-0 right-0 flex w-[160px] items-center gap-3 justify-center text-sm font-medium text-gray-400">
        <ArchiveIcon className="h-4 w-4" />
        Move to Archive
      </div>
      <div
        className={`bg-white ${isDragging ? "" : "transition-transform duration-200"}`}
        style={{ transform: `translateX(${dragX}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {children}
      </div>
    </div>
  );
};

// InsightCard component for displaying basic insight information with expandable details
const InsightCard = ({
  assetId,
  groupId,
  title,
  description,
  selectedAsset,
}: SimpleInsightCardProps) => {
  const navigate = useNavigate();
  const navigateToAssetPage = () => {
    if (groupId && assetId) {
      navigate(`/home/asset-page/${groupId}/${assetId}`);
    }
  };
  const [expanded, setExpanded] = useState(false);
  const [forecastControls, setForecastControls] = useState<any>(null);
  const [graphControls, setGraphControls] = useState<any>(null);
  const [forecastMenuOpen, setForecastMenuOpen] = useState(false);
  const [variationMenuOpen, setVariationMenuOpen] = useState(false);
  const forecastSchema = forecastControls?.foreCastSchema || {};
  const activeForecast = forecastControls?.activeForecast;
  const activeVariationKey = forecastControls?.forecastVariation;
  const variations = forecastSchema?.[activeForecast]?.variations || [];
  const activeVariationLabel =
    variations.find((variation: any) => variation.key === activeVariationKey)?.name ||
    activeVariationKey ||
    "Variation";
  const activeForecastLabel =
    forecastSchema?.[activeForecast]?.name || activeForecast || "Forecast";
  const ActiveForecastIcon = forecastSchema?.[activeForecast]?.Icon;

  return (
    // SwipeReveal wrapper for swipe-to-reveal functionality
    <SwipeReveal>
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="text-left text-[14px] font-medium text-black hover:underline"
            onClick={navigateToAssetPage}
          >
            {title}
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-slate-400">
            {/* Expand/Collapse Button */}
            <button
              type="button"
              className="inline-flex items-center justify-center"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                <CaretUpIcon className="h-1 w-2 text-slate-400" />
              ) : (
                <CaretDownIcon className="h-1 w-2 text-slate-400" />
              )}
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500">{description}</p>

        {expanded && (
          <>
            {/* Graph Section */}
            <div className="mt-3 rounded-[8px] border border-eai-bg-lt2 bg-white">
              <div className="flex items-center justify-end gap-2 border-b border-eai-bg-lt2 px-3 py-2"> {/* This div contains the controls for the graph section */}
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-slate-800"
                  onClick={() => graphControls?.openDataset && graphControls.openDataset()}
                >
                  <CalendarIcon className="h-3 w-3" />
                  Datasets
                </button>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-slate-800"
                    onClick={() => setVariationMenuOpen((prev: boolean) => !prev)}
                    aria-expanded={variationMenuOpen}
                    aria-haspopup="listbox"
                  >
                    <FilterLinesIcon className="h-3 w-3" />
                    {activeVariationLabel}
                    <CaretDownIcon className="h-1 w-2" />
                  </button>
                  {variationMenuOpen && variations.length > 0 && (
                    <div className="absolute right-0 top-full z-10 mt-2 w-56 rounded-[8px] border border-eai-bg-lt2 bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.08)]">
                      {variations.map((variation: any) => (
                        <button
                          key={variation.key}
                          type="button"
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                            variation.key === activeVariationKey
                              ? "bg-slate-50 text-slate-800"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                          onClick={() => {
                            forecastControls?.onVariationChange?.(variation.key);
                            setVariationMenuOpen(false);
                          }}
                          role="option"
                          aria-selected={variation.key === activeVariationKey}
                        >
                          <FilterLinesIcon className="h-3 w-3" />
                          <div>{variation.name || variation.key}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-[8px] border border-eai-bg-lt2 bg-white px-3 py-2 text-xs font-medium text-slate-800"
                    onClick={() => setForecastMenuOpen((prev: boolean) => !prev)}
                    aria-expanded={forecastMenuOpen}
                    aria-haspopup="listbox"
                  >
                    {ActiveForecastIcon ? (
                      <ActiveForecastIcon className="h-4 w-4 stroke-slate-800" />
                    ) : (
                      <FilterLinesIcon className="h-3 w-3" />
                    )}
                    {activeForecastLabel}
                    <CaretDownIcon className="h-1 w-2" />
                  </button>
                  {forecastMenuOpen && Object.keys(forecastSchema).length > 0 && (
                    <div className="absolute right-0 top-full z-10 mt-2 w-56 rounded-[8px] border border-eai-bg-lt2 bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.08)]">
                      {Object.entries(forecastSchema).map(([key, forecast]: any) => {
                        const Icon = forecast?.Icon;
                        const iconClass =
                          key === activeForecast
                            ? "h-4 w-4 stroke-slate-800"
                            : "h-4 w-4 stroke-slate-400";
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                              key === activeForecast
                                ? "bg-slate-100 text-slate-800"
                                : "text-slate-600 hover:bg-slate-100"
                            }`}
                            onClick={() => {
                              forecastControls?.onForecastSelect?.(key);
                              setForecastMenuOpen(false);
                              setVariationMenuOpen(false);
                            }}
                            role="option"
                            aria-selected={key === activeForecast}
                          >
                            {Icon ? <Icon className={iconClass} /> : <FilterLinesIcon className="h-3 w-3" />}
                            <div>{forecast?.name || key}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="relative h-[280px] overflow-hidden"> {/* Container for the AssetGraph with fixed height */}
                <AssetGraph
                  selectedAsset={selectedAsset}
                  onRegisterForecastControls={setForecastControls}
                  onRegisterGraphControls={setGraphControls}
                  onRegisterViewControls={() => {}}
                  onRegisterDownload={() => {}}
                  disableMapView
                  hideHeader
                  hideGraphControls
                  hideContainer
                />
              </div>
            </div>
          </>
        )}
      </div>
    </SwipeReveal>
  );
};

type AssetDashboardCardsProps = {
  assetItems?: any[];
  groupId?: string | null;
};

const AssetDashboardCards = ({ assetItems = [], groupId = null }: AssetDashboardCardsProps) => {
  const insightCards = useMemo(() => {
    if (!assetItems.length || !groupId) {
      return [];
    }

    return assetItems.map((asset: any) => {
      return {
        assetId: String(asset?.id ?? ""),
        selectedAsset: asset,
        title: asset?.name || "Asset",
        description: asset?.description || "No desc"
      };
    });
  }, [assetItems, groupId]);

  return (
    <>
      {/* Insights Cards */}
      <div className="space-y-3">
        {insightCards.map((insight, index) => (
          <InsightCard
            key={`${insight.assetId || insight.title}-${index}`}
            assetId={insight.assetId}
            groupId={groupId}
            title={insight.title}
            description={insight.description}
            selectedAsset={insight.selectedAsset}
          />
        ))}
      </div>
    </>
  );
};

export default AssetDashboardCards;
